import { createSignal, createEffect, For, Show } from "solid-js";
import clsx from "~/util/html/style";
import type { FormData, Provider } from "./types";
import { fetchAvailability } from "./api";
import { useI18n } from "./i18n";

export function ProviderStep(props: {
  formData: FormData;
  onUpdate: (partial: Partial<FormData>) => void;
  onNext: () => void;
  onBack: () => void;
  locationId: string;
  providers: Provider[];
  onProvidersLoaded: (providers: Provider[]) => void;
}) {
  const t = useI18n();
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);

  createEffect(() => {
    if (props.providers.length > 0) {
      setLoading(false);
      return;
    }

    const templateId = props.formData.appointmentTemplateId;
    const patientType = props.formData.patientType;
    if (!templateId || !patientType) return;

    setLoading(true);
    setError(null);

    const onLoaded = props.onProvidersLoaded;
    fetchAvailability(props.locationId, templateId, patientType)
      .then((data) => {
        onLoaded(data.providers);
      })
      .catch(() => {
        setError(t("failed_load_providers"));
      })
      .finally(() => {
        setLoading(false);
      });
  });

  const handleSelect = (providerId: string | null) => {
    props.onUpdate({ providerId });
    props.onNext();
  };

  const loadingText = t("loading_providers");
  const errorText = () => error();

  return (
    <div class="flex flex-col gap-5">
      <h2 class="text-xl font-semibold text-typography-primary">{t("pick_provider")}</h2>

      <Show
        when={!loading()}
        fallback={
          <div class="flex items-center justify-center py-8">
            <div class="size-6 animate-spin rounded-full border-2 border-brand-subtle border-t-brand-primary" />
            <span class="ml-3 text-sm text-typography-secondary">{loadingText}</span>
          </div>
        }
      >
        <Show when={errorText()}>
          {(msg) => <p class="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{msg()}</p>}
        </Show>

        <Show when={props.providers.length > 0}>
          <div class="flex flex-col gap-2.5">
            <button
              type="button"
              class={clsx(
                "rounded-2xl border p-4 text-left transition-all active:scale-[0.98]",
                "border-brand-subtle hover:border-brand-primary/50 hover:bg-brand-light/50",
              )}
              onClick={() => handleSelect(null)}
            >
              <p class="font-medium text-typography-primary">{t("any_provider")}</p>
              <p class="text-sm text-typography-secondary">{t("show_all_available")}</p>
            </button>

            <For each={props.providers}>
              {(provider) => (
                <button
                  type="button"
                  class={clsx(
                    "rounded-2xl border p-4 text-left transition-all active:scale-[0.98]",
                    "border-brand-subtle hover:border-brand-primary/50 hover:bg-brand-light/50",
                  )}
                  onClick={() => handleSelect(provider.id)}
                >
                  <p class="font-medium text-typography-primary">{provider.name}</p>
                </button>
              )}
            </For>
          </div>
        </Show>
      </Show>

      <div class="pt-2">
        <button
          type="button"
          class="rounded-full border border-brand-subtle px-4 py-2.5 text-sm font-medium text-typography-primary transition-colors hover:bg-brand-light active:scale-[0.98]"
          onClick={() => props.onBack()}
        >
          {t("back")}
        </button>
      </div>
    </div>
  );
}
